import { CategoryBadge } from '../lib/categories';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { api, fcfa } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import Header from '../components/Header';
import CountdownTimer from '../components/CountdownTimer';
import { Button } from '../components/ui/button';
import { Gavel, Send, Users, Trophy, MessageCircle, Zap, ShoppingBag, ShoppingCart } from 'lucide-react';
import { cart, imgSrc } from '../lib/cart';
import WebRTCViewer from '../components/WebRTCViewer';
import { ShareLive } from '../components/ShareLive';
import { VerifiedBadge } from '../components/VerifiedBadge';
import { FollowButton } from '../components/FollowButton';

function embedUrl(url) {
  if (!url) return '';
  // Only allow https URLs (prevents javascript: XSS)
  if (!/^https:\/\//i.test(url)) return '';
  if (url.includes('/embed/')) return url;
  const tv = url.match(/tiktok\.com\/@[\w.-]+\/video\/(\d+)/);
  if (tv) return `https://www.tiktok.com/embed/v2/${tv[1]}`;
  const tu = url.match(/tiktok\.com\/(@[\w.-]+)/);
  if (tu) return `https://www.tiktok.com/embed/${tu[1]}`;
  const y = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([\w-]+)/);
  if (y) return `https://www.youtube.com/embed/${y[1]}`;
  return url;
}

export default function LiveRoom() {
  const { liveId } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [live, setLive] = useState(null);
  const [auction, setAuction] = useState(null);
  const [bids, setBids] = useState([]);
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState('');
  const [bidding, setBidding] = useState(false);
  const [following, setFollowing] = useState(false);
  useEffect(() => {
    if (!user || !live?.seller_id) return;
    api.get(`/sellers/${live.seller_id}`).then(({ data }) => setFollowing(!!data.is_following)).catch(() => {});
  }, [user, live?.seller_id]);
  const chatBoxRef = useRef(null);
  const prevExtRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const [{ data: l }, { data: c }] = await Promise.all([
        api.get(`/lives/${liveId}`),
        api.get(`/chat/${liveId}`),
      ]);
      setLive(l);
      setAuction(l.active_auction || null);
      setChat(c);
      if (l.active_auction) {
        const { data: b } = await api.get(`/auctions/${l.active_auction.auction_id}/bids`);
        setBids(b);
        if (l.active_auction.last_extended && !prevExtRef.current) {
          toast(t('time_extended'), { icon: '⚡' });
        }
        prevExtRef.current = !!l.active_auction.last_extended;
      } else {
        setBids([]);
      }
    } catch {}
  }, [liveId, t]);

  useEffect(() => { load(); const id = setInterval(load, 1500); return () => clearInterval(id); }, [load]);
  useEffect(() => { if (chatBoxRef.current) chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight; }, [chat]);

  const placeBid = async () => {
    if (!user) { toast.error(t('login')); nav('/login'); return; }
    if (!auction) return;
    setBidding(true);
    try {
      const { data } = await api.post(`/auctions/${auction.auction_id}/bid`);
      setAuction(data);
      toast.success(`+${fcfa(auction.increment)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setBidding(false); }
  };

  const sendChat = async (e) => {
    e.preventDefault();
    if (!user) { toast.error(t('login')); return; }
    if (!msg.trim()) return;
    try {
      await api.post('/chat', { live_id: liveId, text: msg });
      setMsg(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  };

  if (!live) return <div className="min-h-screen"><Header /><div className="p-10 text-slate-400">Loading…</div></div>;

  return (
    <div className="min-h-screen">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 grid gap-4 lg:grid-cols-[1fr_380px]">
        {/* Left: video + info */}
        <div className="space-y-4">
          <div className="relative rounded-2xl overflow-hidden border border-slate-800 bg-black aspect-video">
            {live.stream_url || live.stream_type === 'webrtc' ? (
              live.stream_type === 'webrtc' ? (
                <div className="absolute inset-0"><WebRTCViewer liveId={live.live_id} /></div>
              ) : (
                <iframe
                  data-testid="live-video-iframe"
                  src={embedUrl(live.stream_url)}
                  title={live.title}
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full" />
              )
            ) : (
              <img src={live.thumbnail} alt="" className="w-full h-full object-cover opacity-70" />
            )}
            <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-bold glow-live">
              <span className="live-dot" />{t('live_now')}
            </div>
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-950/70 backdrop-blur text-white text-xs">
            <Users className="h-3 w-3" /> <span data-testid="live-viewers-count">{live.viewers}</span> {t('viewers')}{live.visits ? <span className="text-slate-400"> · {live.visits} visites</span> : null}
            </div>
          </div>

          <div className="card-elev rounded-2xl p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-display text-xl sm:text-2xl font-bold">{live.title}</h1>
                <div className="flex items-center gap-2">
                  <Link to={`/seller/${live.seller_id}`} data-testid="seller-link" className="text-slate-400 hover:text-amber-400 text-sm">{live.seller_name}</Link>
                  <VerifiedBadge verified={live.seller_verified} testId="verified-live-room" />
                  <FollowButton sellerId={live.seller_id} following={following} size="sm" onChange={(d) => setFollowing(d.following)} />
                </div>
              </div>
              <ShareLive live={live} />
            </div>
            {live.description && <p className="mt-2 text-slate-300 text-sm">{live.description}</p>}
          </div>

          {live.shop_products?.length > 0 && (
            <div className="card-elev rounded-2xl p-4" data-testid="live-shop-products">
              <div className="flex items-center justify-between mb-2">
                <div className="font-display font-bold flex items-center gap-2"><ShoppingBag className="h-4 w-4 text-sky-400" />Articles de la boutique</div>
                <Link to={`/shop?seller=${live.seller_id}`} className="text-xs text-sky-300">Voir tout</Link>
              </div>
              <div className="flex gap-3 overflow-x-auto pretty-scroll pb-1">
                {live.shop_products.map((p) => (
                  <div key={p.product_id} data-testid={`live-shop-${p.product_id}`} className="min-w-[150px] w-[150px] rounded-xl bg-slate-900/60 border border-slate-800 overflow-hidden">
                    <Link to={`/product/${p.product_id}`}>{p.image_url ? <img src={imgSrc(p.image_url)} alt="" className="h-24 w-full object-cover" /> : <div className="h-24 bg-slate-950" />}</Link>
                    <div className="p-2">
                      <div className="text-xs font-bold truncate">{p.name}</div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="font-mono-num text-amber-400 text-xs">{fcfa(p.price)}</span>
                        <button data-testid={`live-add-cart-${p.product_id}`} onClick={() => { cart.add(p); toast.success('Ajouté au panier'); }} className="h-7 w-7 grid place-items-center rounded-full btn-brand text-white"><ShoppingCart className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bidding block — visible on desktop below, on mobile important */}
          <div className="card-elev rounded-2xl p-5 lg:hidden">
            <BidBody auction={auction} bids={bids} onBid={placeBid} bidding={bidding} t={t} />
          </div>
        </div>

        {/* Right: bid + chat */}
        <div className="space-y-4">
          <div className="card-elev rounded-2xl p-5 hidden lg:block">
            <BidBody auction={auction} bids={bids} onBid={placeBid} bidding={bidding} t={t} />
          </div>

          <div className="card-elev rounded-2xl flex flex-col h-[420px] lg:h-[380px]">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-800">
              <MessageCircle className="h-4 w-4 text-slate-400" />
              <span className="font-semibold">{t('chat')}</span>
            </div>
            <div ref={chatBoxRef} className="flex-1 overflow-y-auto pretty-scroll px-4 py-3 space-y-2">
              {chat.length === 0 && <div className="text-xs text-slate-500">No messages yet.</div>}
              {chat.map((m) => (
                <div key={m.msg_id} data-testid={`chat-msg-${m.msg_id}`} className="text-sm">
                  <span className="text-amber-400 font-semibold">{m.user_name}: </span>
                  <span className="text-slate-200">{m.text}</span>
                </div>
              ))}
            </div>
            <form onSubmit={sendChat} className="p-3 border-t border-slate-800 flex gap-2">
              <input
                data-testid="chat-input"
                value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder={t('type_message')}
                className="flex-1 px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white placeholder-slate-500 focus:border-sky-400 outline-none text-sm" />
              <Button type="submit" size="sm" data-testid="chat-send-btn"
                className="btn-brand text-white"><Send className="h-4 w-4" /></Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function BidBody({ auction, bids, onBid, bidding, t }) {
  if (!auction) {
    return (
      <div className="text-center py-6">
        <Gavel className="h-8 w-8 mx-auto text-slate-500 mb-2" />
        <div className="text-slate-400 text-sm">Aucune enchère en cours.</div>
      </div>
    );
  }
  const ended = auction.status !== 'running';
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        {auction.product_image && (
          <img src={auction.product_image} alt="" className="h-14 w-14 rounded-xl object-cover border border-slate-800" />
        )}
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-500">Produit</div>
          <div className="font-display font-bold truncate" data-testid="auction-product-name">{auction.product_name}</div>
          <CategoryBadge category={auction.product_category} size={auction.product_size} brand={auction.product_brand} className="mt-1" />
        </div>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">{t('current_price')}</div>
          <div className="font-mono-num text-3xl sm:text-4xl font-extrabold text-amber-400" data-testid="current-price">{fcfa(auction.current_price)}</div>
          <div className="text-xs text-slate-400 mt-1">
            {t('top_bidder')}: <span className="text-white font-semibold" data-testid="top-bidder">{auction.top_bidder_name || '—'}</span>
          </div>
        </div>
        <CountdownTimer endsAt={auction.ends_at} size={110} />
      </div>

      {ended ? (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/40 text-center">
          <Trophy className="h-6 w-6 mx-auto text-amber-400 mb-1" />
          <div className="text-sm text-slate-300">{t('ended_winner')}: <span className="font-bold text-white">{auction.winner_name || '—'}</span></div>
          <div className="font-mono-num text-amber-400 mt-1">{fcfa(auction.final_price)}</div>
        </div>
      ) : (
        <button
          data-testid="place-bid-button"
          onClick={onBid} disabled={bidding}
          className="w-full py-5 rounded-2xl font-display text-lg font-extrabold bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-500 hover:to-rose-400 text-white bid-pulse disabled:opacity-60 flex items-center justify-center gap-2 shadow-xl shadow-rose-900/40 transition">
          <Zap className="h-5 w-5" />
          {t('place_bid')} +{fcfa(auction.increment).replace(' FCFA','')} FCFA
        </button>
      )}

      <div>
        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">Derniers bids · {auction.bids_count}</div>
        <div className="max-h-32 overflow-y-auto pretty-scroll space-y-1">
          {bids.length === 0 && <div className="text-xs text-slate-500">{t('no_bids')}</div>}
          {bids.map((b) => (
            <div key={b.bid_id} className="flex justify-between text-xs px-2 py-1 rounded bg-slate-900/50">
              <span className="text-slate-300">{b.user_name}</span>
              <span className="font-mono-num text-amber-400">{fcfa(b.amount)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
